import { useState } from 'react'

import {
  useZoneEnableMutation,
  useZoneSuspendWithReasonMutation,
  ZonesListDocument,
  ZoneSuspensionInfoInput,
} from '~/gql/generated/graphql'

type useBulkZoneStatusMutationProps = {
  isSuspended: boolean
  zoneIds: string[]
  onCompleted: (failedIds: string[]) => void
  onError: (err: unknown) => void
}

type UpdateFunctionProps = {
  zoneIds: string[]
  isSuspended: boolean
  suspensionReason?: ZoneSuspensionInfoInput
}

type UpdateSuspendFunctionType = (
  props: UpdateFunctionProps,
) => Promise<string[]>
type UpdateGeneralFunctionType = (
  props: UpdateFunctionProps,
) => Promise<string[]>
type UpdateFunctionType = UpdateSuspendFunctionType | UpdateGeneralFunctionType

export function useBulkZoneStatusMutation({
  onCompleted,
  onError,
}: useBulkZoneStatusMutationProps): [UpdateFunctionType, boolean] {
  const [loading, setLoading] = useState(false)

  const [enableZone] = useZoneEnableMutation({ onError })
  const [suspendZone] = useZoneSuspendWithReasonMutation({ onError })

  const updateFunction: UpdateFunctionType = async ({
    isSuspended,
    zoneIds,
    suspensionReason,
  }) => {
    if (zoneIds.length !== 0) {
      let failedZoneIds: string[] = []
      setLoading(true)

      zoneIds.forEach(async (id, index) => {
        const isLastZone = index + 1 === zoneIds.length

        const refetchQueries = isLastZone ? [ZonesListDocument] : undefined

        let result:
          | Awaited<ReturnType<typeof enableZone>>
          | Awaited<ReturnType<typeof suspendZone>>
        try {
          if (!isSuspended && suspensionReason) {
            result = await suspendZone({
              variables: { id, suspension_reason: suspensionReason },
              refetchQueries,
            })
          } else {
            result = await enableZone({
              variables: { id },
              refetchQueries,
            })
          }
        } finally {
          // Whether it worked or not, set loading false when it gets to the last zone
          if (isLastZone) {
            setLoading(false)
            onCompleted(failedZoneIds)
          }
        }
        if (!result || result?.errors) {
          failedZoneIds = [...failedZoneIds, id]
        }
      })

      return failedZoneIds
    }
    return []
  }

  return [updateFunction, loading]
}
